/**
 * Национальные цифры локали в DOM островов (зеркало App\Support\LocaleDigits
 * на бэке — та же логика токенов, тот же реестр).
 *
 * Пилот bn (2026-09-13): в бангладешской прозе цифры только бенгальские, а
 * смешение с западными внутри предложения в прессе не встречается. Серверный
 * HTML проходит через middleware LocalizeDigits; всё, что рисует React
 * (счётчик «вопрос 3 из 27», таймер, проценты, баллы в карточках результата,
 * рейтинги), приходит из состояния западными цифрами. Правки по каждому месту
 * рендера не масштабируются (91 место по грепу), поэтому — MutationObserver на
 * корне острова (shadow root) и на light DOM: любой новый или изменённый
 * текстовый узел получает национальные цифры. Идемпотентно; React сравнивает
 * с виртуальным деревом, а не с DOM, так что перезапись nodeValue его не ломает
 * (следующий апдейт снова придёт западными — и снова будет заменён).
 *
 * Человекочитаемые атрибуты (placeholder, title, alt, aria-label…) правятся так
 * же, как текст: читатель их видит (подсказка поля поиска «মোট ২৯ পরীক্ষার…»).
 * Не трогаются: служебные атрибуты (value, data-*, href), содержимое
 * input/textarea, script/style/code/pre/kbd/samp, [contenteditable],
 * [translate="no"], [data-no-digits] и токены с латинскими буквами («MMPI-2»,
 * «DASS-21», URL).
 */

const NATIVE_DIGITS: Record<string, string> = {
    bn: '০১২৩৪৫৬৭৮৯',
    // Маратхи (пилот mr 2026-10-08): CLDR deva, пресса Махараштры — देवनागरी; тот же реестр, что в PHP.
    mr: '०१२३४५६७८९',
};

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'KBD', 'SAMP', 'TEXTAREA', 'INPUT', 'NOSCRIPT']);

/** Поля формы: их содержимое (value) — не текст, но placeholder/title у них читаемые. */
const FORM_FIELD_TAGS = new Set(['INPUT', 'TEXTAREA']);

/** Атрибуты, которые читатель видит или слышит; список общий с LocaleDigits::READABLE_ATTRIBUTES. */
export const READABLE_ATTRIBUTES = ['placeholder', 'title', 'alt', 'aria-label', 'aria-description', 'aria-valuetext'];

/** Национальные цифры для локали (по первому сабтегу) или null, если у языка западные. */
export function nativeDigitsFor(locale: string | null | undefined): string | null {
    if (!locale) return null;
    const language = locale.toLowerCase().split(/[-_]/)[0];
    return NATIVE_DIGITS[language] ?? null;
}

/** Цифры в строке по токенам: токен с латинской буквой или «#» остаётся как есть. */
export function localizeDigitsText(text: string, digits: string): string {
    if (!/[0-9]/.test(text)) return text;
    return text.replace(/\S+/g, (token) => {
        if (!/[0-9]/.test(token) || /[A-Za-z]/.test(token) || token.includes('#')) return token;
        return token.replace(/[0-9]/g, (d) => digits[d.charCodeAt(0) - 48]);
    });
}

function hasSkipFlags(e: Element): boolean {
    return e.hasAttribute('data-no-digits') || e.getAttribute('translate') === 'no' || e.hasAttribute('contenteditable');
}

/** Узел внутри пропускаемого поддерева (для элемента — начиная с него самого). */
function isSkipped(node: Node): boolean {
    let el: Node | null = node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode;
    while (el && el.nodeType === Node.ELEMENT_NODE) {
        const e = el as Element;
        if (SKIP_TAGS.has(e.tagName) || hasSkipFlags(e)) return true;
        el = el.parentNode;
    }
    return false;
}

function localizeTextNode(node: Text, digits: string): void {
    const value = node.nodeValue;
    if (!value || !/[0-9]/.test(value) || isSkipped(node)) return;
    const next = localizeDigitsText(value, digits);
    if (next !== value) node.nodeValue = next;
}

/** Один читаемый атрибут элемента; сам input/textarea не пропускается — его placeholder читатель видит. */
function localizeAttribute(el: Element, name: string, digits: string): void {
    const value = el.getAttribute(name);
    if (value === null || !/[0-9]/.test(value)) return;
    if (hasSkipFlags(el) || (SKIP_TAGS.has(el.tagName) && !FORM_FIELD_TAGS.has(el.tagName))) return;
    if (el.parentNode && isSkipped(el.parentNode)) return;
    const next = localizeDigitsText(value, digits);
    if (next !== value) el.setAttribute(name, next);
}

function localizeAttributes(el: Element, digits: string): void {
    if (!el.hasAttributes()) return;
    for (const name of READABLE_ATTRIBUTES) localizeAttribute(el, name, digits);
}

/** Разово пройти поддерево: текстовые узлы и читаемые атрибуты элементов (включая корень). */
export function localizeDigitsIn(root: Node, digits: string): void {
    if (root.nodeType === Node.TEXT_NODE) {
        localizeTextNode(root as Text, digits);
        return;
    }
    if (root.nodeType === Node.ELEMENT_NODE) localizeAttributes(root as Element, digits);
    const doc = root.ownerDocument ?? document;
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
    const nodes: Node[] = [];
    let current = walker.nextNode();
    while (current) {
        nodes.push(current);
        current = walker.nextNode();
    }
    for (const n of nodes) {
        if (n.nodeType === Node.TEXT_NODE) localizeTextNode(n as Text, digits);
        else localizeAttributes(n as Element, digits);
    }
}

const observed = new WeakSet<Node>();

/**
 * Повесить наблюдатель на корень (document.body или shadow root острова):
 * начальный проход + реакция на добавленные узлы, изменённый текст и
 * изменённые читаемые атрибуты. Возвращает функцию отключения.
 * Для локалей с западными цифрами — no-op.
 */
export function observeDigits(root: Node, locale: string | null | undefined = window.App?.locale): () => void {
    const digits = nativeDigitsFor(locale);
    if (!digits || observed.has(root) || typeof MutationObserver === 'undefined') return () => {};
    observed.add(root);

    localizeDigitsIn(root, digits);
    const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            if (m.type === 'characterData') {
                localizeTextNode(m.target as Text, digits);
            } else if (m.type === 'attributes') {
                if (m.attributeName) localizeAttribute(m.target as Element, m.attributeName, digits);
            } else {
                m.addedNodes.forEach((n) => localizeDigitsIn(n, digits));
            }
        }
    });
    observer.observe(root, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: READABLE_ATTRIBUTES,
    });

    return () => {
        observer.disconnect();
        observed.delete(root);
    };
}
